import { toastError } from '@/lib/toast-error';
import { trpc } from '@/lib/trpc';
import type { DeviceFilters } from '../../../shared/types';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';



/**
 * Hook para obtener lista de dispositivos con filtros
 * ✨ Migrated to tRPC with full type safety
 */
export function useDevices(filters?: DeviceFilters) {
  const result = trpc.inventory.getDevices.useQuery(filters || {});

  return {
    data: result.data?.devices || [],
    isLoading: result.isLoading,
    error: result.error,
    refetch: result.refetch,
  };
}

/**
 * Hook para obtener un dispositivo por ID
 * ✨ Migrated to tRPC with full type safety
 */
export function useDevice(deviceId?: string) {
  return trpc.inventory.getDevice.useQuery(deviceId!, {
    enabled: !!deviceId,
  });
}

/**
 * Hook para crear un nuevo dispositivo
 * ✨ Migrated to tRPC with full type safety
 */
export function useCreateDevice() {
  const { t } = useTranslation();
  const utils = trpc.useUtils();

  return trpc.inventory.createDevice.useMutation({
    onSuccess: () => {
      utils.inventory.getDevices.invalidate();
      toast.success(t('hooks.devices.dispositivo_creado_exitosamente'));
    },
    onError: (error) => {
      console.error('Error creating device:', error);
      toastError(error, 'common.toast.error_al_crear_dispositivo');
    },
  });
}

/**
 * Hook para actualizar un dispositivo
 * ✨ Migrated to tRPC with full type safety
 */
export function useUpdateDevice() {
  const { t } = useTranslation();
  const utils = trpc.useUtils();

  return trpc.inventory.updateDevice.useMutation({
    onSuccess: (_, variables) => {
      utils.inventory.getDevices.invalidate();
      utils.inventory.getDevice.invalidate(variables.deviceId);
      toast.success(t('hooks.devices.dispositivo_actualizado_exitosamente'));
    },
    onError: (error) => {
      console.error('Error updating device:', error);
      toastError(error, 'common.toast.error_al_actualizar_dispositivo');
    },
  });
}

/**
 * Hook para eliminar un dispositivo (soft delete)
 * ✨ Migrated to tRPC with full type safety
 */
export function useDeleteDevice() {
  const { t } = useTranslation();
  const utils = trpc.useUtils();

  return trpc.inventory.deleteDevice.useMutation({
    onSuccess: () => {
      utils.inventory.getDevices.invalidate();
      toast.success(t('hooks.devices.dispositivo_marcado_como_baja_total'));
    },
    onError: (error) => {
      console.error('Error deleting device:', error);
      toastError(error, 'common.toast.error_al_eliminar_dispositivo');
    },
  });
}

/**
 * Hook para eliminar permanentemente un dispositivo (hard delete)
 */
export function useHardDeleteDevice() {
  const { t } = useTranslation();
  const utils = trpc.useUtils();

  return trpc.inventory.hardDeleteDevice.useMutation({
    onSuccess: () => {
      utils.inventory.getDevices.invalidate();
      toast.success(t('hooks.devices.dispositivo_eliminado_permanentemente'));
    },
    onError: (error) => {
      console.error('Error hard deleting device:', error);
      toastError(error, 'common.toast.error_al_eliminar_dispositivo');
    },
  });
}

/**
 * Hook para solicitar soporte de QuipTeams para un device (reparación, reasignación, retiro)
 */
export function useRequestQuipteamsSupport() {
  return trpc.inventory.requestQuipteamsSupport.useMutation({
    onError: (error) => {
      console.error('Error requesting Quipteams support:', error);
      toastError(error, 'common.toast.error_al_solicitar_soporte_a_quipteams');
    },
  });
}
